/**
 * `#uc1ChipOverview` — the Overview panel while a constraint chip is pressed (2 Oct chart
 * round, design §1.1). The Priority chip keeps the programme headlines; a constraint
 * replaces them with the answer to "what does this one constraint say", in three parts:
 *
 * * a stacked bar of the units at the current level — flagged, not flagged, not assessed,
 *   with counts, in the constraint's own colour. A class no unit fell in is dropped, the
 *   way every map legend in this pane drops one;
 * * the rule in the words Key indicators already uses (`intel.uc1.essence.<dim>.rule`), so
 *   the threshold is never a number the reader has to go and look up. It is a 25–30 word
 *   sentence, so it is a line under the bar (`#uc1ChipRule`) rather than a stat tile:
 *   `StatTile`'s value slot is for a number, and a paragraph in it wraps to eleven lines
 *   and stretches the whole tile row;
 * * the share of units flagged, and the reference year of the inputs.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUnitRow } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { StackedBar, type StackedBarSegment } from './charts/StackedBar';
import { StatTile, StatTiles } from './charts/StatTile';
import {
  CONSTRAINT_COLORS,
  NEUTRAL_COLOR,
  NOT_ASSESSED_COLOR,
  REFERENCE_YEAR,
} from './uc1Constants';

export interface Uc1ChipOverviewProps {
  rows: IntelUnitRow[];
  /** One of the five constraint ids — the Priority chip never renders this panel. */
  chip: string;
}

/** How many units of each of the three states this constraint has at this level. */
function flagCounts(rows: IntelUnitRow[], chip: string) {
  let flagged = 0;
  let notFlagged = 0;
  let notAssessed = 0;
  for (const row of rows) {
    const flag = row.flags?.[chip];
    if (flag === true) flagged += 1;
    else if (flag === false) notFlagged += 1;
    else notAssessed += 1;
  }
  return { flagged, notFlagged, notAssessed };
}

export function Uc1ChipOverview({ rows, chip }: Uc1ChipOverviewProps) {
  const { t } = useTranslation();
  const counts = flagCounts(rows, chip);
  const label = t(`intel.uc1.dimension.${chip}`, { defaultValue: chip });
  // Assessed units only, which is the denominator the engine's own roll-up uses: a
  // constraint no one could measure in half the programme must not read as half-met.
  const assessed = counts.flagged + counts.notFlagged;
  const sharePct = assessed ? (counts.flagged / assessed) * 100 : null;

  // Every label a class of its own, capitalised the same way: the scorecard's
  // `row.status.notAssessed` is worded for mid-sentence use and read as a typo here.
  const segments: StackedBarSegment[] = [
    {
      id: 'flagged',
      label: t('intel.uc1.legend.flagged'),
      value: counts.flagged,
      color: CONSTRAINT_COLORS[chip] ?? NEUTRAL_COLOR,
    },
    {
      id: 'notFlagged',
      label: t('intel.uc1.legend.notFlagged'),
      value: counts.notFlagged,
      color: NEUTRAL_COLOR,
    },
    {
      id: 'notAssessed',
      label: t('intel.uc1.legend.notAssessedPlain'),
      value: counts.notAssessed,
      color: NOT_ASSESSED_COLOR,
    },
  ].filter((segment) => segment.value > 0);

  return (
    <div id="uc1ChipOverview" className="intel-headlines intel-card">
      <h5 className="intel-card-title">{t('intel.uc1.chart.chipCounts', { name: label })}</h5>
      <StackedBar
        segments={segments}
        ariaLabel={t('intel.uc1.chart.chipCounts', { name: label })}
      />
      <p id="uc1ChipRule" className="intel-rule" data-testid="uc1-chip-rule">
        <span className="intel-rule-label">{t('intel.uc1.tile.rule')}</span>{' '}
        {t(`intel.uc1.essence.${chip}.rule`, { defaultValue: '—' })}
      </p>
      <StatTiles>
        <StatTile
          id="uc1-chip-share"
          value={fmtNum(sharePct, 0)}
          unit={sharePct != null ? '%' : undefined}
          label={t('intel.uc1.tile.shareFlagged')}
        />
        <StatTile
          id="uc1-chip-period"
          value={String(REFERENCE_YEAR)}
          label={t('intel.uc1.tile.period')}
        />
      </StatTiles>
    </div>
  );
}

export default Uc1ChipOverview;
